import { useState, useEffect, useCallback, useMemo, createContext } from 'react'
import ModalLoader from '../components/modal/ModalLoader';
import { AppContextType, AppDialog, BasicProviderProps, UserAuth } from '../types';
import ModalDialog from '../components/modal/ModalDialog';
import { library } from '@fortawesome/fontawesome-svg-core'
import {
    faExpand, faChevronDown, faChevronCircleLeft, faChevronCircleRight, faBoxArchive, faSignIn, faSearch,
    faAdd, faIdCard, faCompress, faDownload, faPlus, faBook, faTrash, faEdit, faFileZipper, faFile,
    faBoxesStacked, faFileDownload, faSignInAlt, faSignOutAlt, faPassport, faMobileAlt, faCogs, faBars,
    faInfoCircle, faWarning, faCheck, faTimes, faParachuteBox, faEye, faFileText, faFolderOpen, faShare,
    faRotateLeft, faArchive, faFileAlt, faSoap, faScroll, faQuestionCircle, faArrowLeft, faArrowRight,
    faBlog, faNewspaper, faUnderline, faHeading, faQuoteLeft, faBold, faItalic, faLink, faImage, faTable,
    faAlignLeft, faAlignCenter, faAlignJustify, faAlignRight, faStrikethrough, faCode,
    faQuestion, faUsers, faKey, faListOl, faNetworkWired, faCopy, faClock, faInfo, faLayerGroup,
    faPowerOff, faGripHorizontal, faListUl, faSortAmountUp, faSortAmountDown, faUser, faBell, faEnvelope,
    faSave, faCancel, faCircleUser, faCaretRight, faCalendarAlt, faFolderClosed, faCloudUploadAlt, faMap,
    faSitemap, faCircleInfo, faPager, faEraser, faFlag, faArrowsLeftRightToLine, faScrewdriverWrench,
    faUserGear, faSms, faUpDown, faFileExcel, faArrowDown, faArrowUp, faPersonBooth, faCreditCard, faFileInvoice,
    faHouseChimney, faHouseChimneyWindow, faRedoAlt, faToolbox, faFileLines, faBookOpen, faChildReaching,
    faFlagCheckered, faPiggyBank, faBaby, faCheckCircle,  faBan,
    faThumbTack,
    faHourglassHalf,
    faClipboardCheck,
    faTicket,
    faThumbTackSlash,
    faToggleOn,
} from '@fortawesome/free-solid-svg-icons'
import useUserStorage from '../hooks/useUserStorage';
import ErrorBoundary from '../components/error/ErrorBoundary';
import { IconButton, Snackbar } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const AppContext = createContext<AppContextType | null>(null);

const initialAppDialog: AppDialog = {
    open: false,
    title: "",
    message: "",
    type: 'info',
    onClose: () => null
}

library.add(
    faExpand, faChevronDown, faChevronDown, faChevronCircleLeft, faChevronCircleRight, faCogs, faPlus,
    faEdit, faTrash, faBoxArchive, faSignIn, faAdd, faIdCard, faSearch, faCompress, faDownload, faFile,
    faPassport, faFileZipper, faBoxesStacked, faSignInAlt, faSignOutAlt, faMobileAlt, faParachuteBox,
    faFileDownload, faFileText, faBars, faEye, faInfoCircle, faWarning, faCheck, faTimes, faBook, faPager,
    faFolderOpen, faFolderClosed, faShare, faRotateLeft, faArchive, faFileAlt, faSoap, faScroll, faQuestionCircle,
    faArrowLeft, faArrowRight, faQuestion, faUsers, faKey, faListOl, faNetworkWired, faCopy, faClock, faEraser,
    faInfo, faLayerGroup, faBlog, faNewspaper, faUnderline, faHeading, faQuoteLeft, faSitemap, faCircleInfo,
    faBold, faItalic, faLink, faImage, faTable, faAlignLeft, faAlignCenter, faAlignJustify, faAlignRight,
    faStrikethrough, faCode, faUser, faPowerOff, faGripHorizontal, faListUl, faSortAmountUp, faSortAmountDown,
    faBell, faEnvelope, faSave, faCancel, faCircleUser, faCaretRight, faCalendarAlt, faCloudUploadAlt, faMap,
    faFlag, faArrowsLeftRightToLine, faScrewdriverWrench, faUserGear, faSms, faUpDown, faFileExcel, faArrowDown,
    faArrowUp, faPersonBooth, faCreditCard, faFileInvoice, faHouseChimney, faHouseChimneyWindow, faRedoAlt, faToolbox,
    faFileLines, faBookOpen, faChildReaching, faFlagCheckered, faPiggyBank, faBaby, faToggleOn,
    faFlag, faArrowsLeftRightToLine, faScrewdriverWrench, faUserGear, faCheckCircle, faBan, faThumbTack,
    faHourglassHalf, faClipboardCheck, faTicket, faThumbTackSlash)

function AppProvider({ children }: BasicProviderProps) {
    const user = useUserStorage<UserAuth>('user')
    const [loading, setLoading] = useState<number>(0)
    const [snackBar, setSnackBar] = useState({ open: false, message: '' })
    const [appDialog, setAppDialog] = useState<AppDialog>(initialAppDialog)

    function notifyError(title: string, message: string, logout?: boolean) {
        setAppDialog({
            open: true,
            type: 'error',
            title: title || 'Aviso',
            message: `${message}`,
            onClose: () => null
        })
        if (logout) {
            user.setData(null)
        }
    }

    const controlLoading = (isLoading: boolean) => {
        setLoading(data => {
            let temp = data + (isLoading ? 1 : -1)
            return temp > 0 ? temp : 0
        })
    }

    const contextData = useMemo<AppContextType>(() => ({
        loading: loading > 0,
        setLoading: controlLoading,
        user,
        setAppDialog: (params: AppDialog) => setAppDialog({ ...params, open: true }),
        appDialogInfo: { isOpen: !!appDialog.open, message: appDialog.message, type: appDialog.type },
        notifyError,
        openSnackBar: (message) => setSnackBar({ open: true, message })
    }), [loading, user, appDialog])


    const handleDialogClose = useCallback(() => {
        appDialog.onClose && appDialog.onClose()
        setAppDialog(initialAppDialog)
    }, [appDialog])

    const handleDialogAccept = useCallback(() => {
        appDialog.onAccept && appDialog.onAccept()
        setAppDialog(initialAppDialog)
    }, [appDialog])


    const handleCloseSnackBar = (_: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBar({ open: false, message: '' });
    };

    useEffect(() => {
        if (appDialog.open) {
            controlLoading(false)
        }
    }, [appDialog.open])

    useEffect(() => {
        if (process.env.NODE_ENV === "production") {
            window.addEventListener('contextmenu', event => event.preventDefault(), true);
            window.addEventListener('keydown', event => {
                if (event.code === "F12") {
                    event.preventDefault();
                }
            }, true)
        }
    }, [])

    return <ErrorBoundary>
        <AppContext.Provider value={contextData}>
            {children}
            <ModalLoader loading={loading > 0} />
            <ModalDialog appDialog={appDialog} onClose={handleDialogClose} onAccept={handleDialogAccept} />
            <Snackbar
                open={snackBar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackBar}
                message={snackBar.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                action={<IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={handleCloseSnackBar}
                >
                    <FontAwesomeIcon icon="times" />
                </IconButton>}
            />
        </AppContext.Provider>
    </ErrorBoundary>
};


export default AppProvider